import { Component, type ErrorInfo, type ReactNode } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { clacUiGlobal as ui } from 'utils/uiGlobal';

/**
 * Error boundary raíz de la aplicación.
 *
 * Sin esto, cualquier excepción lanzada durante el render de React tumba el árbol completo
 * y el usuario cae en la pantalla de error por defecto de React Router — sin contexto, sin
 * forma de recuperarse y sin rastro en consola. El caso que motivó este componente:
 * "Failed to execute 'insertBefore' on 'Node'" al guardar un cuestionario, que no puede
 * capturarse con try/catch porque ocurre en la fase de commit del DOM, no en el handler.
 *
 * Los errores de red/HTTP NO llegan aquí (los maneja cada feature con try/catch). Esto es
 * exclusivamente la última línea de defensa contra crashes de render.
 */

// El fallback vive por encima de <Locales>, así que no puede usar react-intl: si el crash
// viene del propio IntlProvider, FormattedMessage fallaría también. Se lee el idioma del
// <html lang> que Locales mantiene sincronizado.
const COPY = {
  es: {
    title: 'Algo salió mal',
    body: 'La página encontró un error inesperado y no pudo continuar. Tus datos guardados no se vieron afectados.',
    retry: 'Reintentar',
    reload: 'Recargar página',
    detail: 'Detalle técnico'
  },
  en: {
    title: 'Something went wrong',
    body: 'The page hit an unexpected error and could not continue. Your saved data was not affected.',
    retry: 'Try again',
    reload: 'Reload page',
    detail: 'Technical detail'
  }
} as const;

function copy() {
  return document.documentElement.lang === 'en' ? COPY.en : COPY.es;
}

type Props = { children: ReactNode };
type State = { error: Error | null };

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Único registro del crash: sin esto el stack se pierde y solo queda el mensaje
    // del navegador, que no dice en qué componente ocurrió.
    console.error('[AppErrorBoundary]', error, info.componentStack);
  }

  private handleRetry = () => this.setState({ error: null });

  private handleReload = () => window.location.reload();

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const t = copy();

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: ui.semantic.bg.page,
          p: 3
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            bgcolor: ui.semantic.bg.surface,
            border: `1px solid ${ui.semantic.border.subtle}`,
            borderRadius: ui.radius.md,
            boxShadow: ui.shadow.card,
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              fontFamily: ui.fonts.families.heading,
              fontSize: ui.fonts.sizes.xl,
              fontWeight: ui.fonts.weights.bold,
              color: ui.semantic.text.primary,
              mb: 1
            }}
          >
            {t.title}
          </Typography>

          <Typography
            sx={{
              fontFamily: ui.fonts.families.body,
              fontSize: ui.fonts.sizes.md,
              color: ui.semantic.text.secondary,
              mb: 3
            }}
          >
            {t.body}
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'center', mb: 3 }}>
            <Button
              onClick={this.handleRetry}
              sx={{
                textTransform: 'none',
                borderRadius: ui.radius.pill,
                border: `1.5px solid ${ui.semantic.border.default}`,
                color: ui.semantic.text.secondary,
                fontWeight: ui.fonts.weights.semibold,
                px: 2.5,
                py: 1,
                '&:hover': {
                  borderColor: ui.semantic.accent.main,
                  color: ui.semantic.accent.text,
                  bgcolor: ui.semantic.bg.surface
                }
              }}
            >
              {t.retry}
            </Button>
            <Button
              onClick={this.handleReload}
              sx={{
                textTransform: 'none',
                borderRadius: ui.radius.pill,
                bgcolor: ui.semantic.accent.main,
                border: `1.5px solid ${ui.semantic.accent.main}`,
                color: ui.semantic.bg.surface,
                fontWeight: ui.fonts.weights.bold,
                px: 2.5,
                py: 1,
                '&:hover': { bgcolor: ui.semantic.accent.hover, borderColor: ui.semantic.accent.hover }
              }}
            >
              {t.reload}
            </Button>
          </Stack>

          <Box
            component="details"
            sx={{
              textAlign: 'left',
              fontFamily: ui.fonts.families.body,
              fontSize: ui.fonts.sizes.xs,
              color: ui.semantic.text.muted,
              '& summary': { cursor: 'pointer', fontWeight: ui.fonts.weights.medium }
            }}
          >
            <Box component="summary">{t.detail}</Box>
            <Box
              component="pre"
              sx={{
                mt: 1,
                mb: 0,
                p: 1.5,
                bgcolor: ui.semantic.bg.page,
                border: `1px solid ${ui.semantic.border.subtle}`,
                borderRadius: ui.radius.sm,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 160,
                overflowY: 'auto'
              }}
            >
              {error.message}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}
